import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import purchaseOrdersService from '../services/purchaseOrdersService';
import paymentConditionsService from '../services/paymentConditionsService';
import {
  X, Loader, Package, FileText, Calendar, CreditCard, MapPin, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const ReceivePurchaseOrderModal = ({ order, projectId, onClose, onSuccess }) => {
  const [items, setItems] = useState(
    order.items?.map(item => ({
      ...item,
      quantity_received: item.quantity - (item.received_quantity || 0),
      selected: true
    })) || []
  );
  const [fiscalData, setFiscalData] = useState({
    fiscal_number: order.fiscal_number || '',
    fiscal_series: order.fiscal_series || '',
    fiscal_key: order.fiscal_key || '',
    emission_date: order.emission_date || new Date().toISOString().split('T')[0],
    entry_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [paymentCondition, setPaymentCondition] = useState({
    payment_method: order.payment_method || 'SINGLE',
    installments_count: order.installments_count || 1,
    start_date: new Date().toISOString().split('T')[0]
  });
  const [warehouseId, setWarehouseId] = useState(null);

  // Buscar condições de pagamento
  const { data: paymentConditionsData } = useQuery({
    queryKey: ['payment-conditions'],
    queryFn: () => paymentConditionsService.getAllConditions()
  });

  // Mutation para receber OC
  const receiveMutation = useMutation({
    mutationFn: (data) => purchaseOrdersService.receive(order.id, data),
    onSuccess: (response) => {
      toast.success('Ordem de compra recebida com sucesso!');
      if (onSuccess) onSuccess(response.data);
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao receber ordem de compra');
    }
  });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const toggleItem = (itemId) => {
    setItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const updateItemQuantity = (itemId, quantity) => {
    setItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, quantity_received: parseFloat(quantity) || 0 } : item
      )
    );
  };

  const selectedItems = items.filter(i => i.selected && i.quantity_received > 0);
  const totalValue = selectedItems.reduce((sum, item) =>
    sum + (item.quantity_received * item.unit_price), 0
  );

  const handleReceive = () => {
    if (selectedItems.length === 0) {
      toast.error('Selecione pelo menos um item para receber');
      return;
    }

    receiveMutation.mutate({
      items_received: selectedItems.map(item => ({
        item_id: item.id,
        quantity: item.quantity_received,
        warehouse_id: warehouseId
      })),
      fiscal_number: fiscalData.fiscal_number || null,
      fiscal_series: fiscalData.fiscal_series || null,
      fiscal_key: fiscalData.fiscal_key || null,
      emission_date: fiscalData.emission_date || null,
      entry_date: fiscalData.entry_date || null,
      payment_condition_id: paymentCondition.payment_condition_id || null,
      payment_method: paymentCondition.payment_method,
      installments_count: paymentCondition.installments_count,
      start_date: paymentCondition.start_date,
      notes: fiscalData.notes || null
    });
  };

  const paymentConditions = paymentConditionsData?.data || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Receber Ordem de Compra</h2>
              <p className="text-sm text-gray-600">OC #{order.id} - {formatCurrency(order.total_amount || order.total_value)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-blue-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-6">
          {/* Fiscal Data Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Dados da Nota Fiscal
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Número NF</label>
                <input
                  type="text"
                  value={fiscalData.fiscal_number}
                  onChange={(e) => setFiscalData(prev => ({ ...prev, fiscal_number: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                  placeholder="NF-12345"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Série</label>
                <input
                  type="text"
                  value={fiscalData.fiscal_series}
                  onChange={(e) => setFiscalData(prev => ({ ...prev, fiscal_series: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Data Emissão</label>
                <input
                  type="date"
                  value={fiscalData.emission_date}
                  onChange={(e) => setFiscalData(prev => ({ ...prev, emission_date: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Data Entrada</label>
                <input
                  type="date"
                  value={fiscalData.entry_date}
                  onChange={(e) => setFiscalData(prev => ({ ...prev, entry_date: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm text-gray-600 mb-1">Chave NF-e</label>
              <input
                type="text"
                value={fiscalData.fiscal_key}
                onChange={(e) => setFiscalData(prev => ({ ...prev, fiscal_key: e.target.value }))}
                className="w-full border rounded px-3 py-2"
                placeholder="Chave de 44 dígitos"
              />
            </div>
          </div>

          {/* Payment Condition Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Condição de Pagamento
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Forma de Pagamento</label>
                <select
                  value={paymentCondition.payment_method}
                  onChange={(e) => setPaymentCondition(prev => ({
                    ...prev,
                    payment_method: e.target.value,
                    installments_count: e.target.value === 'SINGLE' ? 1 : prev.installments_count
                  }))}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="SINGLE">À Vista</option>
                  <option value="INSTALLMENTS">Parcelado</option>
                </select>
              </div>
              {paymentCondition.payment_method === 'INSTALLMENTS' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Parcelas</label>
                    <select
                      value={paymentCondition.installments_count}
                      onChange={(e) => setPaymentCondition(prev => ({ ...prev, installments_count: parseInt(e.target.value) }))}
                      className="w-full border rounded px-3 py-2"
                    >
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                        <option key={n} value={n}>{n}x</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Primeira Parcela</label>
                    <input
                      type="date"
                      value={paymentCondition.start_date}
                      onChange={(e) => setPaymentCondition(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Valor Total</label>
                <div className="border rounded px-3 py-2 bg-white font-medium text-gray-900">
                  {formatCurrency(totalValue)}
                </div>
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Itens para Receber
            </h3>
            <div className="space-y-2">
              {items.map(item => {
                const pendingQty = item.quantity - (item.received_quantity || 0);
                return (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-3 flex items-center gap-3 ${
                      item.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={() => toggleItem(item.id)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.item_name}</p>
                      <p className="text-sm text-gray-500">
                        Total: {item.quantity} | Já recibido: {item.received_quantity || 0}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Receber:</label>
                      <input
                        type="number"
                        min="0"
                        max={pendingQty}
                        value={item.quantity_received}
                        onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                        className="w-20 border rounded px-2 py-1 text-center"
                      />
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="font-medium">
                        {formatCurrency(item.quantity_received * item.unit_price)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(item.unit_price)}/un
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Observações</label>
            <textarea
              value={fiscalData.notes}
              onChange={(e) => setFiscalData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full border rounded px-3 py-2"
              rows={2}
              placeholder="Observações sobre o recebimento..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedItems.length} item(s) selecionado(s)
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleReceive}
              disabled={selectedItems.length === 0 || receiveMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {receiveMutation.isPending && <Loader className="w-4 h-4 animate-spin" />}
              <Check className="w-4 h-4" />
              Confirmar Recebimento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivePurchaseOrderModal;